/**
 * A folha de pagamento em XLSX do cartão "Pagamentos por Outros Bancos".
 *
 * Papel: aqui fica `lerFolhaXlsx`, que reproduz `handleFolhaImport`
 * (l. 3341–3424): acha a aba e o bloco do mês aberto, soma as colunas de
 * despesa das linhas da casa e devolve um item por nome de coluna.
 *
 * Como usar: quem chama abre o arquivo com `lerPlanilha` e passa a sigla do
 * pagador, que vem da configuração da casa e escolhe a aba. A regra é a da
 * origem, **inclusive o que ela tem de estranho**: as colunas de despesa são
 * posições fixas (`COLUNAS_DE_DESPESA`), e o cabeçalho "Residência" com acento
 * não casa com `residencia` — o `\w` não inclui letra acentuada —, de modo que a
 * coluna da casa fica sendo a 4. Corrigir uma dessas regras mudaria a folha das
 * planilhas que a origem já lia.
 */
import { MESES_POR_EXTENSO, arredondarComoAOrigem } from '@concierge/shared';

import { type PlanilhaLida } from '@/shared/lib/planilha';

/** As colunas que somam despesa, contadas a partir de zero. */
export const COLUNAS_DE_DESPESA = [
  8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 21, 22, 23, 24, 27, 28,
] as const;

/** A casa cujas linhas entram na soma. */
const CASA_DA_FOLHA = 'BOA VISTA';

/** O que `lerFolhaXlsx` devolve: os itens, ou a frase que a tela mostra. */
export type FolhaXlsx = { itens: { nome: string; valor: number }[] } | { erro: string };

/** O texto de uma célula, como a origem o lia (`String(c||'')`). */
function textoDe(celula: unknown): string {
  return celula === null ||
    celula === undefined ||
    celula === '' ||
    celula === 0 ||
    celula === false
    ? ''
    : String(celula);
}

/**
 * Lê a folha do mês aberto numa planilha.
 *
 * A aba é a primeira cujo nome em maiúsculas tem a sigla e o ano; senão, a
 * primeira com a sigla; senão, a primeira. O bloco começa na linha cuja coluna
 * A é o mês alvo (`Março 2026`) e vai até a primeira linha seguinte cuja coluna
 * A é outro texto com quatro dígitos e a coluna B vazia. A linha logo depois do
 * começo é o cabeçalho. Entram na soma as linhas com texto na coluna A e
 * `BOA VISTA` na coluna da casa; cada coluna de despesa soma no nome do
 * cabeçalho dela (ou `col<N>` sem nome), e colunas de mesmo nome somam juntas.
 *
 * @param planilha - A planilha aberta por `lerPlanilha`.
 * @param siglaDoPagador - A sigla que escolhe a aba.
 * @param competencia - O mês aberto, em `AAAA-MM`.
 * @returns Cada nome com o valor arredondado, na ordem em que apareceu, sem os
 *   que não passam de zero; ou o erro do mês que não está na aba e o da soma
 *   zero.
 */
export function lerFolhaXlsx(
  planilha: PlanilhaLida,
  siglaDoPagador: string,
  competencia: string,
): FolhaXlsx {
  const ano = competencia.slice(0, 4);
  const mesAlvo = `${MESES_POR_EXTENSO[Number(competencia.slice(5, 7)) - 1] ?? ''} ${ano}`;
  const sigla = siglaDoPagador.toUpperCase();
  const aba =
    planilha.abas.find((nome) => nome.toUpperCase().includes(sigla) && nome.includes(ano)) ??
    planilha.abas.find((nome) => nome.toUpperCase().includes(sigla)) ??
    planilha.abas[0] ??
    '';
  const linhas = planilha.linhasDa(aba);

  const inicio = linhas.findIndex((linha) => {
    const primeira = linha[0];
    return typeof primeira === 'string' && primeira.trim() === mesAlvo;
  });
  if (inicio === -1) return { erro: `Mês "${mesAlvo}" não encontrado na aba` };

  let fim = linhas.length;
  for (let indice = inicio + 1; indice < linhas.length; indice += 1) {
    const linha = linhas[indice] ?? [];
    const primeira = linha[0];
    if (
      typeof primeira === 'string' &&
      primeira !== mesAlvo &&
      /\d{4}/.test(primeira) &&
      linha[1] === null
    ) {
      fim = indice;
      break;
    }
  }

  const cabecalho = linhas[inicio + 1] ?? [];
  const posicaoDaCasa = cabecalho.findIndex(
    (celula) =>
      textoDe(celula)
        .trim()
        .toLowerCase()
        .replace(/[^\w\s]/g, '') === 'residencia',
  );
  const colunaDaCasa = posicaoDaCasa === -1 ? 4 : posicaoDaCasa;
  const nomeDa = (coluna: number) =>
    textoDe(cabecalho[coluna]).trim() || `col${String(coluna)}`;

  const totais = new Map<string, number>();
  for (const coluna of COLUNAS_DE_DESPESA) {
    if (coluna < cabecalho.length) totais.set(nomeDa(coluna), totais.get(nomeDa(coluna)) ?? 0);
  }
  for (const linha of linhas.slice(inicio + 2, fim)) {
    if (typeof linha[0] !== 'string' || linha[0] === '') continue;
    if (textoDe(linha[colunaDaCasa]).trim().toUpperCase() !== CASA_DA_FOLHA) continue;
    for (const coluna of COLUNAS_DE_DESPESA) {
      if (coluna < linha.length) {
        totais.set(
          nomeDa(coluna),
          (totais.get(nomeDa(coluna)) ?? 0) + (Number(linha[coluna]) || 0),
        );
      }
    }
  }

  const itens = [...totais]
    .map(([nome, valor]) => ({ nome, valor: arredondarComoAOrigem(valor) }))
    .filter((item) => item.valor > 0);
  const total = arredondarComoAOrigem(itens.reduce((soma, item) => soma + item.valor, 0));
  if (total === 0)
    return { erro: `Nenhum lançamento "${CASA_DA_FOLHA}" encontrado para ${mesAlvo}` };
  return { itens };
}
